import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Building2, FileText, RefreshCw, ChevronRight } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { DateRangePicker, rangeQuery } from '../common/DateRangePicker';
import { DEFAULT_CATEGORY_CONFIG } from '../ExpensesPanel';
import { eur } from './DetailKpis';
import { AuthorizedVendors } from './AuthorizedVendors';
import { VendorDuplicates } from '../ExpenseInsights/VendorDuplicates';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtDate = (d) => (d ? d.split('-').reverse().join('/') : '—');

const VendorRow = ({ v, onOpen, i }) => (
  <button onClick={onOpen} className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors" data-testid={`vendor-row-${i}`}>
    <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center shrink-0"><Building2 size={18} className="text-gray-500" /></div>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-gray-900 truncate">{v.name}</p>
      <p className="text-xs text-gray-500 truncate">
        {v.nifs.length ? `${v.nifs.join(', ')} · ` : ''}{v.count} gasto{v.count > 1 ? 's' : ''} · última {fmtDate(v.last_date)}
      </p>
      <div className="flex flex-wrap gap-1 mt-1">
        {v.categories.map(c => (
          <span key={c} className="text-[10px] px-1.5 py-0.5 rounded"
            style={{ backgroundColor: `${DEFAULT_CATEGORY_CONFIG[c]?.color || '#6B7280'}18`, color: DEFAULT_CATEGORY_CONFIG[c]?.color || '#6B7280' }}>
            {DEFAULT_CATEGORY_CONFIG[c]?.label || c}
          </span>
        ))}
      </div>
    </div>
    <div className="text-right shrink-0">
      <p className="font-bold text-gray-900">{eur(v.total)}</p>
      <p className="text-xs text-gray-400 flex items-center justify-end gap-1"><FileText size={11} /> {v.with_invoice}/{v.count}</p>
    </div>
    <ChevronRight size={16} className="text-gray-300 shrink-0" />
  </button>
);

export default function VendorsPanel() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const refresh = () => setTick(t => t + 1);

  const qs = useMemo(() => {
    const p = new URLSearchParams(rangeQuery(range));
    if (q) p.set('q', q);
    return p.toString();
  }, [q, range]);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      fetch(`${API_URL}/api/admin/gastos/proveedores?${qs}`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } })
        .then(r => (r.ok ? r.json() : null)).then(setData).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [qs, tick]);

  const open = (name) => navigate(`/admin/gastos/proveedor/${encodeURIComponent(name)}?${rangeQuery(range)}`);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="vendors-panel">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/gastos')} data-testid="vendors-back-btn"><ArrowLeft size={20} /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Proveedores</h1>
          <p className="text-sm text-gray-500">
            {data ? `${data.vendors.length} proveedores · ${eur(data.total)}` : 'Cargando…'}
          </p>
        </div>
      </div>
      <div className="bg-white rounded-xl border shadow-sm p-3 space-y-3">
        <div className="relative">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input className="pl-8" placeholder="Buscar por nombre o NIF/CIF…" value={q} onChange={e => setQ(e.target.value)} data-testid="vendors-search" />
        </div>
        <DateRangePicker value={range} onChange={setRange} onClear={() => setRange({ from: '', to: '' })} idPrefix="vendors-range" />
      </div>
      <VendorDuplicates key={`dup-${tick}`} onMerged={refresh} />
      <AuthorizedVendors key={`auth-${tick}`} search={q} onChanged={refresh} />
      <p className="text-sm font-semibold text-gray-700 pt-2">Historial de gasto por proveedor</p>
      <div className="bg-white rounded-xl border shadow-sm divide-y overflow-hidden" data-testid="vendors-list">
        {loading && !data && <div className="flex justify-center py-12"><RefreshCw className="animate-spin text-gray-400" /></div>}
        {data?.vendors.length === 0 && <p className="text-center text-gray-400 py-12 text-sm">No hay proveedores con estos filtros</p>}
        {data?.vendors.map((v, i) => <VendorRow key={v.name} v={v} i={i} onOpen={() => open(v.name)} />)}
      </div>
    </div>
  );
}
